import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Link } from 'react-router-dom';

const OverdueTasks = () => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка просроченных задач
  const fetchOverdueTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Получаем задачи с фильтром по просроченным (дата окончания меньше сегодня)
      const today = new Date().toISOString().split('T')[0];
      const url = `${API_URL}${currentLang}/seo-tasks?d_end_to=${today}&limit=10&sort_field=d_end&sort_direction=asc`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Фильтруем только просроченные задачи (статус 0 или 1 и дата окончания прошла)
        const tasks = data.data?.tasks || [];
        const overdue = tasks.filter(task => {
          if (task.status !== 0 && task.status !== 1) return false; // Только ожидающие и в работе
          if (!task.d_end) return false;
          const endDate = new Date(task.d_end);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return endDate < today;
        });
        
        setOverdueTasks(overdue);
      } else {
        throw new Error(data.message || 'Ошибка загрузки просроченных задач');
      }
    } catch (error) {
      console.error('Ошибка загрузки просроченных задач:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverdueTasks();
  }, []);

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };

  // Получение статуса задачи
  const getStatusText = (status) => {
    switch (status) {
      case 0: return currentLang === 'ru' ? 'Ожидает' : 'Pending';
      case 1: return currentLang === 'ru' ? 'В работе' : 'In Progress';
      case 2: return currentLang === 'ru' ? 'Завершена' : 'Completed';
      case 99: return currentLang === 'ru' ? 'Отменена' : 'Cancelled';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      case 99: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Очистка HTML тегов для отображения в таблице
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Вычисление дней просрочки
  const getDaysOverdue = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffTime = today - end;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center justify-center py-6 sm:py-8">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-red-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="text-center py-4">
          <div className="text-red-500 text-xs sm:text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Заголовок */}
      {overdueTasks.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-white">
                  {currentLang === 'ru' ? 'Просроченные задачи' : 'Overdue Tasks'}
                </h3>
                <p className="text-red-100 text-xs sm:text-sm">
                  {currentLang === 'ru' ? 'Кол-во просроченных задач:' : 'Number of overdue tasks:'} {overdueTasks.length}
                </p>
              </div>
            </div>
            <button
              onClick={fetchOverdueTasks}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1 rounded-md transition-all duration-200 flex items-center justify-center sm:justify-start space-x-2 text-xs sm:text-sm"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{currentLang === 'ru' ? 'Обновить' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Контент */}
      {overdueTasks.length === 0 ? (
        <div className="p-6 sm:p-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            {currentLang === 'ru' ? 'Нет просроченных задач!' : 'No overdue tasks!'}
          </h4>
          <p className="text-gray-500 text-sm sm:text-base">
            {currentLang === 'ru' ? 'Все задачи выполняются в срок' : 'All tasks are on schedule'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Название' : 'Name'}
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Прогресс' : 'Progress'}
                </th>
                <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Дата начала' : 'Start Date'}
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Дата окончания' : 'End Date'}
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {currentLang === 'ru' ? 'Просрочено' : 'Overdue'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {overdueTasks.map((task) => {
                const daysOverdue = getDaysOverdue(task.d_end);
                return (
                  <tr key={task.id} className="hover:bg-red-50 transition-colors duration-150">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 max-w-[150px] sm:max-w-xs">
                      <Link 
                        to={`/seo/tasks/${task.id}`}
                        className="text-red-600 hover:text-red-800 font-medium break-words hover:underline flex items-center space-x-2"
                        title={task.name}
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">{task.name}</span>
                      </Link>
                      {task.text && (
                        <div className="text-xs text-gray-500 mt-1 break-words line-clamp-1" title={stripHtml(task.text)}>
                          {stripHtml(task.text)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(task.status)}`}>
                        {getStatusText(task.status)}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        <div className="w-12 sm:w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-red-600 h-2 rounded-full" 
                            style={{ width: `${task.pct}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{task.pct}%</span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(task.d_start)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {formatDate(task.d_end)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-800">
                        {daysOverdue} {currentLang === 'ru' ? 'дн.' : 'days'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Футер с ссылкой на все задачи */}
      {overdueTasks.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {currentLang === 'ru' ? 'Показано' : 'Showing'} {overdueTasks.length} {currentLang === 'ru' ? 'из просроченных задач' : 'overdue tasks'}
            </span>
            <Link
              to="/seo/tasks"
              className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center space-x-1"
            >
              <span>{currentLang === 'ru' ? 'Все задачи' : 'All tasks'}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueTasks; 